/**
 * Multi-Select Dropdown Component
 * Allows users to select multiple options from a predefined list
 * Supports search, custom values (optional), and tag display
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

const MultiSelectDropdown = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options...',
  allowCustom = false,
  required = false,
  helpText = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customValue, setCustomValue] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setCustomValue('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Options may be plain strings (value === label, the common case) or
  // { value, label, description? } objects where the stored value differs
  // from its display label (e.g. value "BDR", label "BDR / SDR"). `value`
  // (the selected values array) always holds plain stored values either way.
  const normalizedOptions = options.map(option =>
    typeof option === 'string' ? { value: option, label: option } : option
  );
  const labelForValue = (v) => normalizedOptions.find(o => o.value === v)?.label || v;

  // Filter against both the primary label and optional supporting details.
  const filteredOptions = normalizedOptions.filter(option =>
    `${option.label} ${option.description || ''}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  // Toggle option selection
  const toggleOption = (option) => {
    if (value.includes(option)) {
      onChange(value.filter(v => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  // Add custom value
  const addCustomValue = () => {
    const trimmedValue = customValue.trim();
    if (trimmedValue && !value.includes(trimmedValue)) {
      onChange([...value, trimmedValue]);
      setCustomValue('');
      setSearchQuery('');
    }
  };

  // Remove selected value
  const removeValue = (valueToRemove) => {
    onChange(value.filter(v => v !== valueToRemove));
  };

  // Handle Enter key for custom values
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && allowCustom && customValue.trim()) {
      e.preventDefault();
      addCustomValue();
    }
  };

  return (
    <div className="garage-multiselect" ref={dropdownRef}>
      {label && (
        <label className="garage-multiselect-label">
          {label}
          {required && <span className="required-indicator"> *</span>}
        </label>
      )}

      {/* Selected Values Display */}
      <div
        className={`garage-multiselect-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {value.length === 0 ? (
          <span className="garage-multiselect-placeholder">{placeholder}</span>
        ) : (
          <div className="garage-multiselect-values">
            {value.slice(0, 3).map((v, idx) => (
              <span key={idx} className="garage-multiselect-tag">
                {labelForValue(v)}
                <button
                  type="button"
                  className="garage-multiselect-tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeValue(v);
                  }}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {value.length > 3 && (
              <span className="garage-multiselect-more">+{value.length - 3} more</span>
            )}
          </div>
        )}
        <ChevronDown size={16} className="garage-multiselect-chevron" />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="garage-multiselect-dropdown">
          {/* Search Box */}
          <div className="garage-multiselect-search">
            <Search size={14} />
            <input
              type="text"
              placeholder={allowCustom ? 'Search or add custom...' : 'Search...'}
              value={allowCustom ? customValue : searchQuery}
              onChange={(e) => {
                if (allowCustom) {
                  setCustomValue(e.target.value);
                  setSearchQuery(e.target.value);
                } else {
                  setSearchQuery(e.target.value);
                }
              }}
              onKeyPress={handleKeyPress}
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="garage-multiselect-options">
            {filteredOptions.length === 0 && !allowCustom ? (
              <div className="garage-multiselect-empty">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`garage-multiselect-option ${value.includes(option.value) ? 'selected' : ''}`}
                  onClick={() => toggleOption(option.value)}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(option.value)}
                    onChange={() => {}}
                  />
                  <span className="garage-multiselect-option-content">
                    <span>{option.label}</span>
                    {option.description && <small>{option.description}</small>}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Add Custom Button */}
          {allowCustom && customValue.trim() && !normalizedOptions.some(o => o.value === customValue.trim()) && (
            <div className="garage-multiselect-custom">
              <button
                type="button"
                className="garage-multiselect-custom-btn"
                onClick={addCustomValue}
              >
                + Add "{customValue.trim()}"
              </button>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      {helpText && <div className="garage-multiselect-help">{helpText}</div>}
    </div>
  );
};

export default MultiSelectDropdown;
